import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { UserDto } from '@act/shared/data-transfer-objects';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import TranscriptionDrawerContext from './PostCallSummaryContext';
import { callsAppSelectors } from 'redux/slices/callsSlice';
import { DateTime } from 'luxon';
import { timeFormatter } from '@act/shared/utilities';
import { transcriptFormatter } from 'utilities/formatters/transcript-formatter';
import BubbleChat from './BubbleTranscript';
import PlainTextChat from './PlainTextTranscript';
import { ToastProvider } from '../../app/ReusableToast';
import PostCallFeedback from './post-call-feedback/PostCallFeedback';

interface TranscriptionDrawerProps {
  selectedMember: UserDto;
  open: boolean;
  onClose: (transcription: string | null) => void;
}

const TranscriptionDrawer_OLD = ({
  selectedMember,
  open,
  onClose,
}: TranscriptionDrawerProps) => {
  const [viewMode, setViewMode] = useState<'speech-bubble' | 'plain-text'>(
    'speech-bubble'
  );

  const { voiceCallId } = useContext(TranscriptionDrawerContext);
  const voiceCall = callsAppSelectors.useSelectCallById(voiceCallId);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(
    voiceCall?.recording?.isAccurateTranscription === null
  );

  const voiceCallRecording = voiceCall?.recording;

  useEffect(() => {
    if (voiceCallRecording?.isAccurateTranscription === null) {
      setIsFeedbackOpen(true);
    }
  }, [voiceCallRecording?.isAccurateTranscription]);

  const transcriptLines = transcriptFormatter.transcriptSplitter(
    voiceCallRecording?.recordingTranscription ?? ''
  );

  function changeViewMode(mode: 'speech-bubble' | 'plain-text') {
    if (mode) {
      setViewMode(mode);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={() => onClose(null)}
      anchor="right"
      PaperProps={{
        sx: {
          marginTop: '50px',
          width: '40%',
          padding: '24px',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <Typography variant="headlineMedium">
          Call with {selectedMember?.name}
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          sx={{ marginLeft: 'auto' }}
          onChange={(_, value) => changeViewMode(value)}
        >
          <ToggleButton value="speech-bubble" defaultChecked>
            <MessageOutlinedIcon />
          </ToggleButton>
          <ToggleButton value="plain-text">
            <NotesOutlinedIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={() => onClose(null)}>
          <CloseOutlinedIcon />
        </IconButton>
      </Box>
      <Divider sx={{ margin: '24px 0' }} />
      <Box>
        {voiceCall?.timeStarted && (
          <Typography variant="titleMedium">
            {timeFormatter.getDateString({
              date: DateTime.fromMillis(voiceCall?.timeStarted),
              includeTime: true,
            })}
            {voiceCall?.timeEnded && (
              <>
                {' - '}
                {timeFormatter
                  .epochToHumanReadableTime(voiceCall?.timeEnded, true)
                  ?.toLocaleLowerCase()}
              </>
            )}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '20px',
            overflow: 'hidden',
            height: 'calc(100vh - 250px)',
          }}
        >
          <Box
            sx={{
              overflow: 'auto',
              flex: 1,
              paddingBottom: '20px',
              paddingRight: '20px',
            }}
          >
            {viewMode === 'speech-bubble' && (
              <BubbleChat transcriptLines={transcriptLines} />
            )}
            {viewMode === 'plain-text' && (
              <PlainTextChat transcriptLines={transcriptLines} />
            )}
          </Box>
          <ToastProvider>
            {isFeedbackOpen && (
              <PostCallFeedback
                feedbackType="transcription"
                voiceCallRecording={voiceCallRecording}
                onClose={() => setIsFeedbackOpen(false)}
              />
            )}
          </ToastProvider>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TranscriptionDrawer_OLD;
